import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';
import { ArrowRight, LayoutGrid, Pin, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import BBALoader from '../components/BBALoader';
import ReportAccessModal from '../components/crm/ReportAccessModal';
import PinnedReportCards from '../components/crm/PinnedReportCards';
import { ReportBackdrop, usePinnedReportCards } from '../components/crm/reportCards';
import useReportAccess from '../hooks/useReportAccess';
import { REPORT_GROUPS, REPORTS_BY_ID, canOpenReport, isReportAdminRole } from '../utils/reportCatalog';
import {
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassSearch,
  GlassSelect,
  GlassAccentButton,
} from '../components/glass/GlassUi';

const GROUP_TITLES = Object.fromEntries(REPORT_GROUPS.map((group) => [group.id, group.title]));

// Grid = every report as a tile; My view = the cards this staff member pinned inside the reports
const VIEWS = [
  { id: 'grid', label: 'Grid view', icon: LayoutGrid },
  { id: 'mine', label: 'My view', icon: Pin },
];

// Hover shine sweep across a tile (the rise-in comes from the glass kit's glass-rise)
const pageStyles = `
  .reports-cell .reports-shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%);
    transform: translateX(-120%);
    transition: transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }
  .dark .reports-cell .reports-shine {
    background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.06) 50%, transparent 70%);
  }
  .reports-cell:hover .reports-shine {
    transform: translateX(120%);
  }
  @media (prefers-reduced-motion: reduce) {
    .reports-cell, .reports-cell * { transition: none !important; }
  }
`;

// Same surface as the kit's GlassCard (which renders a div); tiles are buttons, so it is composed here
const TILE = 'relative rounded-2xl border border-white/80 bg-[#FBFAF8]/70 shadow-[0_10px_40px_-18px_rgba(28,25,22,0.22),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-2xl dark:border-white/[0.08] dark:bg-[#1C1916]/50 dark:bg-gradient-to-b dark:from-white/[0.05] dark:to-white/[0.01] dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(255,255,255,0.06)]';

// group-hover (not hover) so the tile keeps its hover look while the access count is under the pointer
const TILE_HOVER = 'group-hover:border-[#F80707]/25 group-hover:shadow-[0_22px_48px_-20px_rgba(248,7,7,0.35),inset_0_1px_0_rgba(255,255,255,0.8)] dark:group-hover:border-[#F80707]/30 dark:group-hover:shadow-[0_28px_60px_-26px_rgba(248,7,7,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]';

const Reports = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ loaded: false, role: null, view: 'grid' });
  const [staff, setStaff] = useState(null);
  const { accessDocs, loaded: accessLoaded } = useReportAccess();
  const { keys: pinKeys, loaded: pinsLoaded, togglePin } = usePinnedReportCards();
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [accessReportId, setAccessReportId] = useState(null);

  // Role and the chosen view live on the staff member's own internal_users doc
  useEffect(() => {
    if (!currentUser) return undefined;
    return onSnapshot(
      doc(db, 'internal_users', currentUser.uid),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setProfile({
          loaded: true,
          role: data.role || null,
          view: data.reportsHubView === 'mine' ? 'mine' : 'grid',
        });
      },
      (error) => {
        console.error('Error loading user profile:', error);
        setProfile((prev) => ({ ...prev, loaded: true }));
      }
    );
  }, [currentUser]);

  // Every staff member, for the access counts and the access list
  useEffect(() => {
    getDocs(collection(db, 'internal_users'))
      .then((snap) => setStaff(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((error) => {
        console.error('Error loading staff:', error);
        setStaff([]);
      });
  }, []);

  const isAdmin = isReportAdminRole(profile.role);
  const uid = currentUser?.uid;
  const view = profile.view;

  const setView = async (next) => {
    if (!uid || next === view) return;
    setProfile((prev) => ({ ...prev, view: next }));
    try {
      await updateDoc(doc(db, 'internal_users', uid), { reportsHubView: next });
    } catch (error) {
      console.error('Error saving reports view:', error);
    }
  };

  // A report shows when the viewer can open it and it matches the search and group filter
  const isVisible = useCallback((report) => {
    const q = search.trim().toLowerCase();
    const groupId = report.groupId || REPORTS_BY_ID[report.id]?.groupId;
    return canOpenReport(report, accessDocs[report.id], profile.role, uid) &&
      (groupFilter === 'all' || groupId === groupFilter) &&
      (!q || `${report.title} ${report.description} ${GROUP_TITLES[groupId]}`.toLowerCase().includes(q));
  }, [search, groupFilter, accessDocs, profile.role, uid]);

  const visibleGroups = useMemo(() => REPORT_GROUPS
    .map((group) => ({ ...group, reports: group.reports.filter((r) => isVisible({ ...r, groupId: group.id })) }))
    .filter((group) => group.reports.length > 0), [isVisible]);

  const accessCount = (report) => (staff
    ? staff.filter((user) => canOpenReport(report, accessDocs[report.id], user.role, user.id)).length
    : null);

  const clearFilters = () => {
    setSearch('');
    setGroupFilter('all');
  };

  const filtering = search.trim() !== '' || groupFilter !== 'all';
  const accessReport = accessReportId ? REPORTS_BY_ID[accessReportId] : null;
  const ready = profile.loaded && accessLoaded && (view !== 'mine' || pinsLoaded);

  // Running index so tiles stagger in order across every group.
  let tileIndex = 0;

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <style>{pageStyles}</style>

      <GlassPageHeader
        title="Reports"
        controls={(
          <>
            <GlassSearch
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports…"
              className="w-full sm:w-72"
            />
            <GlassSelect
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              className="w-full sm:w-60"
              aria-label="Filter by group"
            >
              <option value="all">All groups</option>
              {REPORT_GROUPS.map((group) => (
                <option key={group.id} value={group.id}>{group.emoji} {group.title}</option>
              ))}
            </GlassSelect>
            <div className="inline-flex rounded-full border border-black/[0.06] bg-white/50 p-1 backdrop-blur-xl dark:border-white/[0.08] dark:bg-white/[0.05]" role="group" aria-label="Reports view">
              {VIEWS.map(({ id, label, icon: Icon }) => {
                const active = view === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setView(id)}
                    title={label}
                    aria-label={label}
                    aria-pressed={active}
                    className={`grid h-8 w-8 place-items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F80707]/50 ${active
                      ? 'bg-[#F80707] text-white shadow-[0_6px_16px_-8px_rgba(248,7,7,0.8)]'
                      : 'text-gray-500 hover:text-[#F80707] dark:text-white/50 dark:hover:text-[#FF4D40]'}`}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.9} />
                  </button>
                );
              })}
            </div>
          </>
        )}
      />

      {!ready ? (
        <div className="flex justify-center py-24">
          <BBALoader size={96} />
        </div>
      ) : view === 'mine' ? (
        <PinnedReportCards pinKeys={pinKeys} isVisible={isVisible} togglePin={togglePin} />
      ) : visibleGroups.length === 0 ? (
        <GlassCard className="px-6 py-16 text-center">
          <div className="mb-3 text-4xl" role="img" aria-hidden="true">🔍</div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            {!filtering ? 'No reports have been shared with you yet' : search ? <>No reports match “{search}”</> : 'No reports in this group'}
          </h2>
          {filtering && (
            <>
              <p className="mt-1 text-sm text-gray-500 dark:text-white/[0.45]">Try a different keyword or group.</p>
              <GlassAccentButton onClick={clearFilters} className="mt-5">
                Clear filters
              </GlassAccentButton>
            </>
          )}
        </GlassCard>
      ) : (
        <div className="space-y-10">
          {visibleGroups.map((group) => (
            <section key={group.id}>
              {/* Left-aligned group label with a separator line fading out to the right */}
              <div className="glass-rise mb-5 flex items-center gap-5" style={{ animationDelay: `${tileIndex * 45}ms` }}>
                <h2 className="shrink-0 text-2xl font-semibold tracking-tight text-[#1C1916] dark:text-white">{group.title}</h2>
                <span className="h-px flex-1 bg-gradient-to-r from-[#F80707]/40 via-[#1C1916]/15 to-transparent dark:from-[#F80707]/50 dark:via-white/10" />
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                {group.reports.map((report) => {
                  const delay = tileIndex++ * 45;
                  const count = accessCount(report);
                  return (
                    // Rise on the wrapper so the finished animation never pins the cell's hover lift
                    <div key={report.id} className="glass-rise flex" style={{ animationDelay: `${delay}ms` }}>
                      <div className="reports-cell group relative flex w-full transition-transform duration-300 ease-out hover:-translate-y-0.5">
                        <button
                          type="button"
                          onClick={() => navigate(report.path)}
                          title={report.description}
                          className={`${TILE} ${TILE_HOVER} flex w-full flex-col items-center overflow-hidden px-3 pb-4 pt-6 text-center transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F80707]/50`}
                        >
                          {/* Soft BBA red bloom on hover */}
                          <span className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[#F80707]/[0.18] opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100 dark:bg-[#F80707]/25" />
                          <span className="reports-shine" />

                          <span className="relative text-2xl leading-none transition-transform duration-300 ease-out group-hover:-rotate-6 group-hover:scale-110" role="img" aria-hidden="true">{report.emoji}</span>

                          <h3 className="relative mt-2 flex-1 text-sm font-medium tracking-tight text-[#1C1916] dark:text-white">{report.title}</h3>

                          <span className="relative mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 transition-colors duration-300 group-hover:text-[#F80707] dark:text-white/40 dark:group-hover:text-[#FF4D40]">
                            Open report
                            <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                          </span>
                        </button>

                        {count != null && (
                          <button
                            type="button"
                            onClick={() => setAccessReportId(report.id)}
                            title={`${count} ${count === 1 ? 'person has' : 'people have'} access${isAdmin ? ' · click to manage' : ''}`}
                            aria-label={`${count} with access to ${report.title}`}
                            className="absolute right-1.5 top-1.5 z-10 inline-flex items-center gap-0.5 rounded-full px-1.5 py-1 text-[11px] font-medium tabular-nums text-gray-400 transition-colors hover:bg-[#F80707]/10 hover:text-[#F80707] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F80707]/50 dark:text-white/40 dark:hover:bg-[#F80707]/15 dark:hover:text-[#FF4D40]"
                          >
                            <Users className="h-3 w-3" strokeWidth={2} />
                            {count}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {accessReport && staff && (
        <ReportAccessModal
          report={accessReport}
          accessDoc={accessDocs[accessReport.id]}
          users={staff}
          canEdit={isAdmin}
          currentUid={uid}
          onClose={() => setAccessReportId(null)}
        />
      )}
    </GlassPage>
  );
};

export default Reports;
